import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Play, HardDrive, CalendarClock, ArchiveRestore, Database,
  BellRing, AlertTriangle, Trash2, ShieldCheck, Save,
} from "lucide-react";

const RETENTION_PRESETS = [
  { days: 0,   label: "No retention" },
  { days: 30,  label: "30 days" },
  { days: 90,  label: "90 days" },
  { days: 180, label: "6 months" },
  { days: 365, label: "1 year" },
  { days: 1095,label: "3 years" },
  { days: 1825,label: "5 years" },
];

const STATE_META = {
  no_retention: { label: "No retention",    cls: "!bg-slate-100 !text-slate-700 !border-slate-200" },
  active:       { label: "Active",          cls: "!bg-emerald-100 !text-emerald-800 !border-emerald-200" },
  alert_90:     { label: "Alert · ≤90d",    cls: "!bg-amber-50 !text-amber-700 !border-amber-200" },
  alert_60:     { label: "Alert · ≤60d",    cls: "!bg-amber-100 !text-amber-800 !border-amber-300" },
  alert_30:     { label: "Alert · ≤30d",    cls: "!bg-orange-100 !text-orange-800 !border-orange-300" },
  alert_7:      { label: "Alert · ≤7d",     cls: "!bg-red-100 !text-red-800 !border-red-300" },
  expired:      { label: "Expired",         cls: "!bg-red-600 !text-white !border-red-700" },
};

export default function AdminBackups() {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [drafts, setDrafts] = useState({});  // { [id]: retention_days_input }

  const load = () => api.get("/admin/backups").then((r) => {
    setItems(r.data || []);
    const d = {};
    (r.data || []).forEach((b) => { d[b.id] = b.retention_days ?? 0; });
    setDrafts(d);
  });
  useEffect(() => { load(); }, []);

  const runBackup = async () => {
    setBusy(true);
    try { await api.post("/admin/backups"); toast.success("Backup captured"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const saveRetention = async (id) => {
    const v = Number(drafts[id] ?? 0);
    try {
      await api.patch(`/admin/backups/${id}/retention`, { retention_days: v });
      toast.success(v > 0 ? `Retention set to ${v} days` : "Retention disabled");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const deleteBackup = async (id) => {
    if (!window.confirm("Delete this backup? This is manual and permanent — the row is removed from the database. The underlying storage file (if any) must be removed separately from Emergent storage.")) return;
    try {
      await api.delete(`/admin/backups/${id}`);
      toast.success("Backup deleted");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const alerts = useMemo(
    () => items.filter((b) => ["alert_7","alert_30","alert_60","alert_90","expired"].includes(b.retention_state)),
    [items]
  );
  const expired = alerts.filter((b) => b.retention_state === "expired");

  const totalSize = items.reduce((s, b) => s + (b.size_bytes || 0), 0);

  return (
    <div data-testid="admin-backups">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <span className="eyebrow">External storage</span>
          <h1 className="mt-2 text-3xl text-navy flex items-center gap-3">
            <HardDrive size={26} className="text-gold" /> Backup Management
          </h1>
        </div>
        <button onClick={runBackup} disabled={busy}
                className="btn-gold disabled:opacity-40" data-testid="run-backup">
          <Play size={14} /> {busy ? "Running…" : "Run backup now"}
        </button>
      </div>
      <span className="divider-gold" />

      {/* In-admin retention alert banner */}
      {alerts.length > 0 && (
        <div className="card-elev p-4 mb-6 border-l-4 border-amber-400 bg-amber-50/40"
             data-testid="backup-alert-banner">
          <div className="flex items-start gap-3">
            <BellRing className="text-amber-600 shrink-0 mt-0.5" size={20} />
            <div className="text-sm text-navy">
              <div className="font-semibold text-navy">
                Retention alerts — {alerts.length} backup(s) need attention
              </div>
              <div className="text-navy/70 mt-1">
                {expired.length > 0 && <span className="me-3"><b>{expired.length}</b> expired · eligible for manual deletion.</span>}
                {alerts.length - expired.length > 0 && <span>{alerts.length - expired.length} approaching expiry (≤90d).</span>}
                {" "}Email notifications are sent automatically at 90 / 60 / 30 / 7 days and on expiry.
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-8">
        <div className="card-elev p-5" data-testid="backup-stat-total">
          <div className="flex items-center justify-between">
            <span className="text-xs uppercase tracking-widest text-navy/60">Total snapshots</span>
            <Database size={18} className="text-gold" />
          </div>
          <div className="mt-3 text-3xl font-serif text-navy">{items.length}</div>
        </div>
        <div className="card-elev p-5" data-testid="backup-stat-size">
          <div className="flex items-center justify-between">
            <span className="text-xs uppercase tracking-widest text-navy/60">Combined size</span>
            <HardDrive size={18} className="text-gold" />
          </div>
          <div className="mt-3 text-3xl font-serif text-navy">{(totalSize / 1024).toFixed(1)} KB</div>
        </div>
        <div className="card-elev p-5" data-testid="backup-stat-latest">
          <div className="flex items-center justify-between">
            <span className="text-xs uppercase tracking-widest text-navy/60">Latest</span>
            <CalendarClock size={18} className="text-gold" />
          </div>
          <div className="mt-3 text-navy font-medium text-sm">
            {items.length > 0 ? new Date(items[0].created_at).toLocaleString() : "—"}
          </div>
        </div>
      </div>

      <div className="card-elev p-6 mb-8">
        <h2 className="text-lg text-navy flex items-center gap-2">
          <ArchiveRestore size={18} className="text-gold" /> Retention policy
        </h2>
        <span className="divider-gold" />
        <p className="text-sm text-navy/70">
          Admin sets a retention period per backup. Alerts are emailed
          <b> 90, 60, 30, and 7 days</b> before expiry, and again <b>on expiry</b>.
          After expiry, backups are <b>eligible for manual deletion only</b> —
          nothing is auto-deleted.
        </p>
      </div>

      <div className="card-elev">
        <div className="p-4 text-sm text-navy/80 border-b border-navy/10">Backup history</div>
        <div className="divide-y divide-navy/10">
          {items.length === 0 && (
            <div className="p-8 text-center text-navy/50">No backups yet — click Run backup now.</div>
          )}
          {items.map((b) => {
            const meta = STATE_META[b.retention_state] || STATE_META.no_retention;
            const draft = drafts[b.id] ?? (b.retention_days ?? 0);
            const dirty = Number(draft) !== Number(b.retention_days ?? 0);
            return (
              <div key={b.id} className="p-5 flex flex-col md:flex-row md:items-start gap-4"
                   data-testid={`backup-row-${b.id}`}>
                <div className="flex items-start gap-4 min-w-0 flex-1">
                  <HardDrive className="text-gold mt-1" />
                  <div className="min-w-0">
                    <div className="text-navy font-medium flex items-center flex-wrap gap-2">
                      <span className="chip !bg-white !text-navy !border-navy/20 !py-0 !px-1.5 text-[10px]">
                        {(b.type || "manual").toUpperCase()}
                      </span>
                      {new Date(b.created_at).toLocaleString()}
                      <span className={`chip ${meta.cls}`} data-testid={`backup-state-${b.id}`}>
                        {meta.label}{typeof b.days_left === "number" ? ` · ${b.days_left}d` : ""}
                      </span>
                    </div>
                    <div className="text-xs text-navy/60 mt-1">
                      {Object.entries(b.counts || {}).map(([k, v]) => `${k}:${v}`).join(" · ") || "—"}
                    </div>
                    <div className="text-xs text-navy/50 mt-1 truncate">
                      {b.storage} · {((b.size_bytes || 0) / 1024).toFixed(1)} KB
                      {b.expires_at && <> · expires {new Date(b.expires_at).toLocaleDateString()}</>}
                      {(b.alerts_sent || []).length > 0 && (
                        <> · alerts sent: {b.alerts_sent.map((n) => n === 0 ? "expired" : `${n}d`).join(", ")}</>
                      )}
                    </div>
                  </div>
                </div>

                <div className="flex flex-col items-end gap-2 md:min-w-[280px]">
                  <div className="flex items-center gap-2">
                    <ShieldCheck size={14} className="text-navy/40" />
                    <select
                      className="border border-navy/20 rounded-sm text-xs px-2 py-1 bg-white text-navy"
                      value={draft}
                      onChange={(e) => setDrafts((d) => ({ ...d, [b.id]: Number(e.target.value) }))}
                      data-testid={`backup-retention-select-${b.id}`}
                    >
                      {RETENTION_PRESETS.map((p) => (
                        <option key={p.days} value={p.days}>{p.label}</option>
                      ))}
                      {!RETENTION_PRESETS.some((p) => p.days === Number(draft)) && (
                        <option value={Number(draft)}>{draft} days</option>
                      )}
                    </select>
                    <input
                      type="number" min="0" step="1"
                      className="w-20 border border-navy/20 rounded-sm text-xs px-2 py-1 bg-white text-navy"
                      value={draft}
                      onChange={(e) => setDrafts((d) => ({ ...d, [b.id]: Number(e.target.value) }))}
                      placeholder="days"
                      data-testid={`backup-retention-input-${b.id}`}
                    />
                    <button
                      className="btn-gold !py-1 !px-2 text-xs disabled:opacity-40"
                      onClick={() => saveRetention(b.id)}
                      disabled={!dirty}
                      data-testid={`backup-retention-save-${b.id}`}
                    >
                      <Save size={12} /> Save
                    </button>
                  </div>
                  <button
                    className="text-xs inline-flex items-center gap-1.5 px-2 py-1 rounded-sm border border-red-300 text-red-700 hover:bg-red-50 disabled:opacity-30 disabled:cursor-not-allowed"
                    disabled={!b.delete_eligible}
                    title={b.delete_eligible ? "Delete expired backup" : "Only expired backups can be deleted"}
                    onClick={() => deleteBackup(b.id)}
                    data-testid={`backup-delete-${b.id}`}
                  >
                    <Trash2 size={12} /> Delete{b.delete_eligible ? "" : " (locked)"}
                  </button>
                  <span className="chip !text-[10px] !py-0 !px-1.5"
                        style={{ background: b.status === "success" ? "#DCFCE7" : "#FEE2E2",
                                 color: b.status === "success" ? "#065F46" : "#991B1B",
                                 border: "1px solid rgba(0,0,0,.05)" }}>
                    {b.status}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {expired.length > 0 && (
        <div className="mt-6 text-xs text-navy/60 flex items-start gap-2">
          <AlertTriangle size={12} className="text-amber-600 mt-0.5" />
          <span>
            Nothing is deleted automatically. Delete each expired backup manually from the row above.
          </span>
        </div>
      )}
    </div>
  );
}
